import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { Icon } from '../../components';
import { AppRoutes, KNOWLEDGE_HUB_TYPE_CONFIG } from '../../constants';
import {
  useAnalyticsLoader,
  useAppSelector,
  useFeatureFlag,
  useGetUserPermissions,
  useReviewLoader,
  useSettingsLoader,
  useSimulationsLoader,
} from '../../hooks';
import { MainPageLayout } from '../../layouts';
import { default as SubPageLayout } from '../../layouts/SubPageLayout';
import {
  AccountPage,
  AnalyticsPage,
  CoachingRubricsPage,
  CreateCandidatePage,
  HiringPage,
  IntegrationsCallbackPage,
  IntegrationsPage,
  KnowledgeHubPage,
  ManageCoachingTemplatePage,
  ManagePromptTemplatePage,
  ManageScorecardTemplatePage,
  MessagePage,
  NotFoundPage,
  OrganizationPage,
  PeoplePage,
  PromptTemplatesPage,
  ProspectPage,
  ReviewPage,
  ScorecardsPage,
  SimulationsPage,
} from '../../pages';
import {
  KnowledgeHubRecordType,
  LD_FeatureFlags,
  OrganizationStatus,
  Permissions,
  ScoringAndCoachingTabs,
  SettingsTab,
} from '../../types';
import { conditionalArray } from '../../utils';
import ProtectedRoute from './ProtectedRoute';

/** Provides router context to the application. */
const CustomRouterProvider: React.FC = () => {
  const analyticsLoader = useAnalyticsLoader();
  const simulationsLoader = useSimulationsLoader();
  const reviewLoader = useReviewLoader();
  const settingsLoader = useSettingsLoader();

  const isUnderConstruction = useFeatureFlag(LD_FeatureFlags.UNDER_CONSTRUCTION);
  const coachingRubricsFF = useFeatureFlag(LD_FeatureFlags.RELEASE_COACHING_RUBRICS);
  const knowledgeHubFF = useFeatureFlag(LD_FeatureFlags.RELEASE_KNOWLEDGE_HUB);

  const { user, organization } = useAppSelector((state) => state.auth);
  const permissions = user?.permissions || [];

  const { canUserCreateSims } = useGetUserPermissions();

  const isSuperAdmin = permissions.includes(Permissions.ADMIN_ACCESS);
  const canManageKnowledgeHub = permissions.includes(Permissions.MANAGE_KNOWLEDGE_HUB);
  const canModifyScorecardTemplates = permissions.includes(Permissions.MODIFY_SCORECARD_TEMPLATE);
  const canModifyCoachingTemplates = permissions.includes(Permissions.MODIFY_COACHING_TEMPLATE);

  // Settings permissions
  const canConnectGong = permissions.includes(Permissions.CONNECT_EXTERNAL_INTEGRATION);
  const canViewHiring = permissions.includes(Permissions.VIEW_CANDIDATE);
  const canViewOrgSettings = permissions.includes(Permissions.MANAGE_ORG_SETTINGS);

  const isHiringModuleEnabled = !!organization?.isHiringModuleEnabled;
  const isTrialOrg = organization?.status === OrganizationStatus.TRIAL;

  const isHiringRouteEnabled = canViewHiring && isHiringModuleEnabled;

  const canManageScorecards = canModifyScorecardTemplates && !isTrialOrg;
  const canManageCoaching = coachingRubricsFF && canModifyCoachingTemplates && !isTrialOrg;

  // Router configuration using pre-defined route constants.
  const router = createBrowserRouter([
    {
      // Protected route that renders the main layout if the user is authenticated.
      path: AppRoutes.HOME,
      element: (
        <ProtectedRoute>
          <MainPageLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          // Redirect root path to Simulations page.
          path: AppRoutes.HOME,
          element: <Navigate to={AppRoutes.SIMULATIONS} replace />,
        },
        {
          // Route for the Simulations page.
          path: AppRoutes.SIMULATIONS,
          element: <SimulationsPage />,
          loader: simulationsLoader,
        },
        {
          // Since we combined the /practice and /quiz routes into a single /simulations route,
          // we need this route to ensure existing bookmarks and links continue working.
          path: '/practice/*',
          element: <Navigate to={`${AppRoutes.SIMULATIONS}${window.location.search}`} replace />,
        },
        {
          // Since we combined the /practice and /quiz routes into a single /simulations route,
          // we need this route to ensure existing bookmarks and links continue working.
          path: '/quiz/*',
          element: <Navigate to={`${AppRoutes.SIMULATIONS}${window.location.search}`} replace />,
        },
        ...conditionalArray(canUserCreateSims, {
          // Route for creating or editing a prospect.
          path: `${AppRoutes.PROSPECT}/:personaId?`,
          element: <ProspectPage />,
        }),
        ...conditionalArray(isSuperAdmin, {
          // Route for viewing, creating and editing prompt templates.
          // Only super admins can access this route.
          path: AppRoutes.PROMPT_TEMPLATES,
          element: <PromptTemplatesPage />,
        }),
        ...conditionalArray(isSuperAdmin, {
          // Route for creating a new prompt template.
          // Only super admins can access this route.
          path: AppRoutes.PROMPT_TEMPLATES_CREATE,
          element: <ManagePromptTemplatePage />,
        }),
        ...conditionalArray(isSuperAdmin, {
          // Route for editing a specific prompt template by ID.
          // Only super admins can access this route.
          path: `${AppRoutes.PROMPT_TEMPLATES}/:promptTemplateId`,
          element: <ManagePromptTemplatePage />,
        }),
        {
          // Since we renamed the /call-history route to /review,
          // we need this route to ensure existing bookmarks and links continue working.
          path: '/call-history/*',
          element: <Navigate to={`${AppRoutes.REVIEW}${window.location.search}`} replace />,
        },
        {
          // Route for the Review page, with optional callSid parameter
          path: `${AppRoutes.REVIEW}/:callSid?`,
          element: <ReviewPage />,
          loader: reviewLoader,
        },
        {
          // Route for the Analytics page.
          path: AppRoutes.ANALYTICS,
          element: <AnalyticsPage />,
          loader: analyticsLoader,
        },
        // Route for Scoring and Coaching pages.
        ...conditionalArray(canManageScorecards || canManageCoaching, {
          path: AppRoutes.SCORING_AND_COACHING,
          element: <SubPageLayout />,
          children: [
            {
              index: true,
              element: (
                <Navigate
                  to={canManageScorecards ? ScoringAndCoachingTabs.SCORECARDS : ScoringAndCoachingTabs.COACHING_RUBRICS}
                  replace
                />
              ),
            },
            ...conditionalArray(canManageScorecards, {
              path: ScoringAndCoachingTabs.SCORECARDS,
              element: <ScorecardsPage />,
            }),
            ...conditionalArray(canManageCoaching, {
              path: ScoringAndCoachingTabs.COACHING_RUBRICS,
              element: <CoachingRubricsPage />,
            }),
          ],
        }),
        ...conditionalArray(canManageScorecards, {
          path: AppRoutes.SCORECARDS,
          element: <ManageScorecardTemplatePage />,
          children: [{ path: 'create' }, { path: ':scorecardTemplateId' }],
        }),
        ...conditionalArray(canManageCoaching, {
          path: AppRoutes.COACHING_RUBRICS,
          element: <ManageCoachingTemplatePage />,
          children: [{ path: 'create' }, { path: ':coachingTemplateId' }],
        }),
        {
          path: AppRoutes.SETTINGS,
          element: <SubPageLayout />,
          loader: settingsLoader,
          children: [
            {
              index: true,
              element: <Navigate to={SettingsTab.ACCOUNT} replace />,
            },
            {
              path: SettingsTab.ACCOUNT,
              element: <AccountPage />,
            },
            {
              path: SettingsTab.PEOPLE,
              element: <PeoplePage />,
            },
            ...conditionalArray(canViewOrgSettings, {
              path: SettingsTab.ORGANIZATION,
              element: <OrganizationPage />,
            }),
            ...conditionalArray(isHiringRouteEnabled, {
              path: SettingsTab.HIRING,
              element: <HiringPage />,
            }),
            ...conditionalArray(canConnectGong, {
              path: SettingsTab.INTEGRATIONS,
              element: <IntegrationsPage />,
            }),
          ],
        },
        ...conditionalArray(isHiringRouteEnabled, {
          path: `${AppRoutes.SETTINGS}/${SettingsTab.HIRING}/create`,
          element: <CreateCandidatePage />,
        }),
        ...conditionalArray(canManageKnowledgeHub && knowledgeHubFF, {
          path: AppRoutes.KNOWLEDGE_HUB,
          element: <SubPageLayout />,
          children: [
            {
              index: true,
              element: <Navigate to={KNOWLEDGE_HUB_TYPE_CONFIG[KnowledgeHubRecordType.PRIORITY].path} replace />,
            },
            ...Object.values(KnowledgeHubRecordType).map((tab) => ({
              path: KNOWLEDGE_HUB_TYPE_CONFIG[tab].path,
              element: <KnowledgeHubPage type={tab} />,
            })),
          ],
        }),
        {
          path: AppRoutes.GONG_CALLBACK,
          element: <IntegrationsCallbackPage />,
        },
        {
          path: AppRoutes.SALESLOFT_CALLBACK,
          element: <IntegrationsCallbackPage />,
        },
      ],
    },
    {
      // Route for handling 404 not found errors.
      path: AppRoutes.NOT_FOUND,
      element: <NotFoundPage />,
    },
  ]);

  if (isUnderConstruction) {
    return (
      <MessagePage
        icon={Icon.TRAFFIC_CONE}
        title="Routine maintenance"
        message="Building new features just for you! Please try again in a few hours."
      />
    );
  }

  return <RouterProvider router={router} />;
};

export default CustomRouterProvider;
